import type { CheckboxProperties, FillTitleType, FormElement, FormStructure } from '../model/form.types';
import type { FieldValue } from '../model/types';
import { parseCivilDate } from './civil-date';
import { isEmptyValue, selectedStrings } from './definitions/shared';
import { migratedElements, walkFields } from './field-walk';
import { getFieldDefinition } from './registry';

export const INDEXING_SEPARATOR = ' / ';
export const RECORD_TITLE_CODE_SEPARATOR = ' - ';

const FILL_TITLE_TYPES: readonly FillTitleType[] = ['FIRST_TITLE', 'SECOND_TITLE', 'THIRD_TITLE', 'FOURTH_TITLE'];
export type IndexerTitleType = Extract<FillTitleType, 'FIRST_TITLE' | 'SECOND_TITLE' | 'THIRD_TITLE'>;

export const INDEXER_TITLE_TYPES: readonly IndexerTitleType[] = ['FIRST_TITLE', 'SECOND_TITLE', 'THIRD_TITLE'];

export interface IndexingFields {
  indexerId: string | null;
  titleIds: Partial<Record<FillTitleType, string>>;
}

export type IndexingFieldsResult =
  | { ok: true; fields: IndexingFields }
  | { ok: false; reason: 'duplicated-indexer' | 'duplicated-title' };

function isFillTitleType(value: unknown): value is FillTitleType {
  return FILL_TITLE_TYPES.includes(value as FillTitleType);
}

// A FILL_TITLE carries fillTitleType as its own display setting, not as a configuration flag.
function flaggableFields(structure: FormStructure): FormElement[] {
  return [...walkFields(migratedElements(structure))].filter((element) => element.type !== 'FILL_TITLE');
}

function flagsOf(element: FormElement): { indexer: boolean; titleType: FillTitleType | null } {
  const titleType: unknown = element.properties.fillTitleType;
  return {
    indexer: element.properties.indexerField === true,
    titleType: isFillTitleType(titleType) ? titleType : null,
  };
}

function readFlags(elements: FormElement[]): IndexingFieldsResult {
  let indexerId: string | null = null;
  const titleIds: Partial<Record<FillTitleType, string>> = {};
  for (const element of elements) {
    const { indexer, titleType } = flagsOf(element);
    if (indexer) {
      if (indexerId !== null) return { ok: false, reason: 'duplicated-indexer' };
      indexerId = element.id;
    }
    if (titleType !== null) {
      if (titleIds[titleType] !== undefined) return { ok: false, reason: 'duplicated-title' };
      titleIds[titleType] = element.id;
    }
  }
  return { ok: true, fields: { indexerId, titleIds } };
}

export function findIndexingFields(structure: FormStructure): IndexingFieldsResult {
  return readFlags(flaggableFields(structure));
}

// The type and the name are part of it: retyping a flagged field breaks the complete IF and OS
// flags, and the configuration names its fields, so a renamed one no longer matches the field the
// saved records were titled by.
export function indexingFlagSignature(structure: FormStructure): string {
  const flagged = flaggableFields(structure).flatMap((element) => {
    const { indexer, titleType } = flagsOf(element);
    const name = element.properties.name ?? '';
    return indexer || titleType !== null ? [[element.id, element.type, name, indexer, titleType].join('|')] : [];
  });
  return flagged.sort().join(';');
}

export function isLegacyTitleForm(structure: FormStructure): boolean {
  const elements = [...walkFields(migratedElements(structure))];
  const hasTitleElement = elements.some((element) => element.type === 'INDEXER_TITLE' || element.type === 'FILL_TITLE');
  if (!hasTitleElement) return false;
  return flaggableFields(structure).every((element) => {
    const { indexer, titleType } = flagsOf(element);
    return !indexer && titleType === null;
  });
}

function checkedOptionTexts(element: FormElement, value: FieldValue): string[] {
  const selected = new Set(selectedStrings(value));
  const options = (element.properties as CheckboxProperties).options ?? [];
  return options.map((option) => option.text).filter((text) => selected.has(text));
}

// Two titles that differ only by repeated inner spaces read the same to the person, and the title is
// unique, so every indexer text is stored with its spaces collapsed.
export function normalizeIndexerText(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

export function indexingText(element: FormElement, value: FieldValue | undefined): string {
  const stored = value ?? null;
  if (isEmptyValue(stored)) return '';
  if (element.type === 'CHECKBOX') return normalizeIndexerText(checkedOptionTexts(element, stored).join(', '));
  const definition = getFieldDefinition(element);
  if (!definition) return '';
  return normalizeIndexerText(definition.cells(element, stored).join(', '));
}

// Read through the builder default like compile does: a field nobody touched shows its default
// ("Valor") on the sheet with no key in values, and the indexer must say what the sheet says.
function titleTexts(
  structure: FormStructure,
  values: Record<string, FieldValue>,
): Partial<Record<FillTitleType, string>> | null {
  const elements = flaggableFields(structure);
  const found = readFlags(elements);
  if (!found.ok) return null;
  const byId = new Map(elements.map((element) => [element.id, element]));
  const texts: Partial<Record<FillTitleType, string>> = {};
  for (const titleType of FILL_TITLE_TYPES) {
    const element = byId.get(found.fields.titleIds[titleType] ?? '');
    if (!element) continue;
    const stored = values[element.id];
    const value = stored === undefined ? (getFieldDefinition(element)?.emptyValue(element) ?? null) : stored;
    texts[titleType] = indexingText(element, value);
  }
  return texts;
}

export function composeNonSequentialIndexer(structure: FormStructure, values: Record<string, FieldValue>): string {
  const texts = titleTexts(structure, values);
  if (texts === null) return '';
  return INDEXER_TITLE_TYPES.map((titleType) => texts[titleType] ?? '')
    .filter((text) => text.length > 0)
    .join(INDEXING_SEPARATOR);
}

export function composeFlaggedTitles(
  structure: FormStructure,
  values: Record<string, FieldValue>,
): Partial<Record<FillTitleType, string>> {
  return titleTexts(structure, values) ?? {};
}

export function formatDatedIndexer(civilDate: string, sequence: number): string {
  if (parseCivilDate(civilDate) === null) throw new Error(`Invalid civil date: ${civilDate}`);
  if (!Number.isInteger(sequence) || sequence < 1) throw new Error(`Invalid indexer sequence: ${sequence}`);
  return `${civilDate.replace(/-/g, '')}${String(sequence).padStart(3, '0')}`;
}

export function nextIndexerSequence(last: number | null, start: number | null): number {
  return Math.max((last ?? 0) + 1, start ?? 1);
}

export function composeRecordTitle(code: string, indexer: string, rest: readonly string[] = []): string {
  return `${code}${RECORD_TITLE_CODE_SEPARATOR}${[indexer, ...rest].join(INDEXING_SEPARATOR)}`;
}
